import { Controller } from "@hotwired/stimulus";

// Owns the chip's trigger label(s), the dismiss flow, and (when name: is
// passed server-side) the hidden form inputs + auto-submit lifecycle.
//
// Peers react to `filter-chip:cleared` via @window listeners with a
// `contains()` guard so sibling chips never cross-trigger. The chip itself
// listens to dropdown:shown/hidden the same way to drive the
// snapshot+diff+submit cycle.
export default class extends Controller {
  static targets = [
    "label",
    "operatorLabel",
    "valueLabel",
    "valuePanel",
    "operatorPanel",
    "dismissFocus",
    "hiddenInputs",
    "operatorInput",
  ];
  static values = {
    attribute: String,
    labelSingular: String,
    labelPlural: String,
    operatorLabels: Object,
    valueInputName: String,
    autoSubmit: { type: Boolean, default: true },
  };

  connect() {
    // Seed from DOM so an operator-only event before any value change still
    // picks the right singular/plural form.
    this.valueCount = this._currentValues().length;
    // Map so 3-seg chips (two dropdowns) track each segment's snapshot
    // independently; a single ivar would be clobbered on interleaved events.
    this._openSnapshots = new Map();
  }

  onSelectionChange(event) {
    if (this.hasLabelTarget) {
      this._updateOneSegmentLabel(event);
    } else {
      this._updateThreeSegment(event);
    }
    this._syncHiddenInputs();
  }

  // Clears value selection only; operator stays put (clearing a chip clears
  // its selection, not its rule shape). The dismiss button itself disappears
  // via CSS `:has()` once selections drain. Peers + host react to the
  // dispatched events. No-op when nothing is selected — CSS feature drift
  // can leave the button clickable; guard prevents a spurious form submit.
  dismiss() {
    if (this._currentValues().length === 0) return;

    this._clearValueSelection();

    if (this.hasLabelTarget) {
      this.labelTarget.textContent = this.attributeValue;
    } else {
      this.valueCount = 0;
      this._updateValueLabel();
      this._updateOperatorLabel();
      this._updateOperatorOptionLabels();
    }

    this._syncHiddenInputs();

    // The dismiss button is about to vanish via CSS; hand keyboard focus to
    // the chip's primary trigger so it doesn't fall to <body>.
    if (this.element.contains(document.activeElement)) {
      this._focusTriggerAfterDismiss();
    }

    // Cleared cascades into dropdown#hideIfOwner. Drop all snapshots so
    // the resulting dropdown:hidden events short-circuit instead of submitting.
    this._openSnapshots.clear();

    this._dispatchChanged();
    this.dispatch("cleared", {
      detail: { attribute: this.attributeValue },
      bubbles: true,
    });

    this._submitForm();
  }

  // Snapshot before-state on open so the close handler can decide whether
  // anything actually changed. Self-scoped: dropdowns live on the chip
  // (1-seg) or on child segments (3-seg), so we check that the event
  // originated inside the chip's own tree.
  onDropdownShown(event) {
    if (!event.target || !this.element.contains(event.target)) return;
    this._openSnapshots.set(event.target, this._currentSnapshot());
  }

  onDropdownHidden(event) {
    if (!event.target || !this.element.contains(event.target)) return;

    const snapshot = this._openSnapshots.get(event.target);
    this._openSnapshots.delete(event.target);
    if (snapshot == null) return;

    const current = this._currentSnapshot();
    if (this._snapshotEquals(snapshot, current)) return;

    this._dispatchChanged();
    this._submitForm();
  }

  // ─── 1-segment ──────────────────────────────────────────────────────────

  _updateOneSegmentLabel(event) {
    const count = (event.detail?.selected || []).filter(Boolean).length;
    this.labelTarget.textContent = count === 0
      ? this.attributeValue
      : `${this.attributeValue} (${count})`;
  }

  // ─── 3-segment ──────────────────────────────────────────────────────────

  _updateThreeSegment(event) {
    const segment = event.target.closest("[data-filter-chip-role]");
    const role = segment?.dataset.filterChipRole;

    if (role === "value") {
      const selected = (event.detail?.selected || []).filter(Boolean);
      this.valueCount = selected.length;
      this._updateValueLabel();
      // Operator plural switch is driven by value count, so retitle both
      // the trigger and the dropdown rows.
      this._updateOperatorLabel();
      this._updateOperatorOptionLabels();
    } else if (role === "operator") {
      this._updateOperatorLabel();
    }
  }

  _updateValueLabel() {
    const count = this.valueCount;

    if (count === 0) {
      this.valueLabelTarget.textContent = `${this.labelPluralValue} (choose)`;
      return;
    }

    // Mirrors Ruby's `pluralize(count, label)`: only count=1 uses singular.
    const inflected = count === 1 ? this.labelSingularValue : this.labelPluralValue;
    this.valueLabelTarget.textContent = `${count} ${inflected}`;
  }

  _updateOperatorLabel() {
    // Defensive only: operator panel runs with selectionRequired=true.
    const selectedRow = this._selectedOperatorRow();
    if (!selectedRow) return;

    const text = this._resolveOperatorLabel(selectedRow.dataset.value);
    if (text != null) this.operatorLabelTarget.textContent = text;
  }

  _updateOperatorOptionLabels() {
    if (!this.hasOperatorPanelTarget) return;

    const rows = this.operatorPanelTarget.querySelectorAll(
      '[role="option"][data-value]',
    );
    rows.forEach((row) => {
      const text = this._resolveOperatorLabel(row.dataset.value);
      if (text == null) return;
      const labelEl = row.querySelector(".filter-option-list__item-label");
      if (labelEl) labelEl.textContent = text;
    });
  }

  _resolveOperatorLabel(value) {
    const entry = this.operatorLabelsValue?.[value];
    if (!entry) return null;

    const usePlural = this.valueCount >= 2;
    return usePlural && entry.plural ? entry.plural : entry.singular;
  }

  _clearValueSelection() {
    if (!this.hasValuePanelTarget) return;

    this.valuePanelTarget
      .querySelectorAll(".filter-option-list__checkbox:checked")
      .forEach((cb) => {
        cb.checked = false;
      });

    this.valuePanelTarget
      .querySelectorAll('[role="option"][aria-selected="true"]')
      .forEach((row) => {
        row.setAttribute("aria-selected", "false");
      });
  }

  _focusTriggerAfterDismiss() {
    if (this.hasDismissFocusTarget) this.dismissFocusTarget.focus();
  }

  // ─── form integration ──────────────────────────────────────────────────

  // Rebuilds value inputs from the panel's current selection. The operator
  // input is updated in place so its DOM identity (and target binding) is
  // stable across renders.
  _syncHiddenInputs() {
    if (!this.hasHiddenInputsTarget) return;
    if (!this.valueInputNameValue) return;

    const container = this.hiddenInputsTarget;
    container
      .querySelectorAll(`input[name="${this.valueInputNameValue}"]`)
      .forEach((el) => el.remove());

    const operator = this.hasOperatorInputTarget ? this.operatorInputTarget : null;

    this._currentValues().forEach((value) => {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = this.valueInputNameValue;
      input.value = value;
      container.insertBefore(input, operator);
    });

    if (operator) {
      const selectedRow = this._selectedOperatorRow();
      if (selectedRow) operator.value = selectedRow.dataset.value;
    }
  }

  _currentValues() {
    if (!this.hasValuePanelTarget) return [];

    const checked = this.valuePanelTarget.querySelectorAll(
      ".filter-option-list__checkbox:checked",
    );
    if (checked.length > 0) return Array.from(checked).map((cb) => cb.value);

    const selectedRows = this.valuePanelTarget.querySelectorAll(
      '[role="option"][aria-selected="true"]',
    );
    return Array.from(selectedRows).map((row) => row.dataset.value);
  }

  _currentOperator() {
    const row = this._selectedOperatorRow();
    return row ? row.dataset.value : null;
  }

  _selectedOperatorRow() {
    if (!this.hasOperatorPanelTarget) return null;
    return this.operatorPanelTarget.querySelector(
      '[role="option"][aria-selected="true"]',
    );
  }

  _currentSnapshot() {
    return {
      values: [...this._currentValues()].sort(),
      operator: this._currentOperator(),
    };
  }

  _snapshotEquals(a, b) {
    if (a.operator !== b.operator) return false;
    if (a.values.length !== b.values.length) return false;
    return a.values.every((v, i) => v === b.values[i]);
  }

  _dispatchChanged() {
    this.dispatch("changed", {
      detail: {
        attribute: this.attributeValue,
        name: this.valueInputNameValue || null,
        values: this._currentValues(),
        operator: this._currentOperator(),
      },
      bubbles: true,
    });
  }

  _submitForm() {
    if (!this.autoSubmitValue) return;
    if (!this.hasHiddenInputsTarget) return;

    const form = this.element.closest("form");
    if (!form) return;

    this._syncQueryFromSearch(form);
    form.requestSubmit();
  }

  // Search lives outside the results frame and updates independently. Hidden
  // `query` fields baked in at last full-page render (e.g. after archive
  // redirect_back) go stale once the search box is cleared. Use the live
  // input as the source of truth.
  _syncQueryFromSearch(form) {
    const searchInput = document.querySelector("[data-table-search-target='search']");
    const query = searchInput?.value?.trim() || "";
    const input = form.querySelector('input[name="query"]');

    if (query) {
      if (input) {
        input.value = query;
      } else {
        const hidden = document.createElement("input");
        hidden.type = "hidden";
        hidden.name = "query";
        hidden.value = query;
        form.appendChild(hidden);
      }
    } else if (input) {
      input.remove();
    }
  }
}
